import React from 'react';
import { UserCheck, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';
import GradientButton from '@/components/ui/GradientButton';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { LightboxImage, SidebarContext } from '../types';

interface ProfilePictureSidebarProps {
  context: SidebarContext<LightboxImage>;
  isSettingProfilePic: boolean;
  isCurrentImageActiveProfile: boolean;
  onChooseProfilePicture: () => Promise<void>;
  onDeleteClick: () => void;
  onRemoveClick?: () => void;
}

export const ProfilePictureSidebar: React.FC<ProfilePictureSidebarProps> = ({
  context,
  isSettingProfilePic,
  isCurrentImageActiveProfile,
  onChooseProfilePicture,
  onDeleteClick,
  onRemoveClick
}) => {
  const {
    item,
    hasPrevious,
    hasNext,
    isLoadingMore,
    hasNextPage,
    onPrevious,
    onNext,
    onLoadMore
  } = context;

  return (
    <div className="w-full md:w-[250px] flex-shrink-0 md:border-l border-accent/10 flex flex-col md:h-full">
      {/* Main content container */}
      <div className="flex flex-col md:h-full">
        {/* Navigation buttons at the top */}
        <div className="border-b border-accent/10 bg-background">
          <div className="p-3 md:p-4 flex gap-2">
            <motion.button
              className={cn(
                "flex items-center justify-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-all flex-1",
                hasPrevious
                  ? "bg-black hover:bg-gray-900 text-white dark:bg-black dark:hover:bg-gray-900 dark:text-white cursor-pointer"
                  : "bg-gray-800 text-gray-400 dark:bg-gray-800 dark:text-gray-600 opacity-50 cursor-not-allowed"
              )}
              onClick={hasPrevious ? onPrevious : undefined}
              disabled={!hasPrevious}
              whileHover={hasPrevious ? { scale: 1.02 } : {}}
              whileTap={hasPrevious ? { scale: 0.98 } : {}}
              data-test-id="sidebar-previous-button"
              type="button"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </motion.button>
            
            <motion.button
              className={cn(
                "flex items-center justify-center gap-1 px-3 py-2 rounded-md text-sm font-medium transition-all flex-1",
                (hasNext && !isLoadingMore)
                  ? "bg-black hover:bg-gray-900 text-white dark:bg-black dark:hover:bg-gray-900 dark:text-white cursor-pointer"
                  : isLoadingMore
                    ? "bg-gray-700 text-white dark:bg-gray-700 dark:text-white cursor-default"
                    : "bg-gray-800 text-gray-400 dark:bg-gray-800 dark:text-gray-600 opacity-50 cursor-not-allowed"
              )}
              onClick={() => {
                if (hasNext && onNext && !isLoadingMore) {
                  onNext();
                } else if (hasNextPage && !isLoadingMore && onLoadMore) {
                  onLoadMore();
                }
              }}
              disabled={(!hasNext && !hasNextPage) || isLoadingMore}
              whileHover={(hasNext && !isLoadingMore) ? { scale: 1.02 } : {}}
              whileTap={(hasNext && !isLoadingMore) ? { scale: 0.98 } : {}}
              data-test-id="sidebar-next-button"
              type="button"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </motion.button>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 p-3 md:p-4 md:overflow-y-auto">
          {/* Profile Picture Info */}
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Your profile picture is visible to all users on the platform
            </p>

            {/* Status Badge or Use Image Button */}
            {isCurrentImageActiveProfile ? (
              <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                <UserCheck className="h-3 w-3 mr-1" />
                Current Profile Picture
              </div>
            ) : (
              <GradientButton
                variant="green"
                icon={<UserCheck className="h-4 w-4" />}
                onClick={onChooseProfilePicture}
                isLoading={isSettingProfilePic}
                disabled={isSettingProfilePic}
                testId="choose-profile-picture-button"
                className="w-fit"
              >
                {isSettingProfilePic ? 'Setting...' : 'Use Image'}
              </GradientButton>
            )}
          </div>
        </div>
        
        {/* Fixed bottom section with action buttons */}
        {onRemoveClick && (
          <div className="border-t border-accent/10 bg-background">
            <div className="p-3 md:p-4">
              <GradientButton
                variant="red"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={onDeleteClick}
                isLoading={context.isDeleteLoading}
                disabled={context.isDeleteLoading}
                fullWidth
                testId="delete-profile-picture-button"
              >
                {context.isDeleteLoading ? 'Deleting...' : 'Delete'}
              </GradientButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}; 